import { X } from 'lucide-react';
import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@/lib/cn';

import { Button } from './button';

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  /** 'sheet' slides in from the right (side panel); 'dialog' is centred. */
  mode?: 'dialog' | 'sheet' | undefined;
  width?: 'sm' | 'md' | 'lg' | 'xl' | undefined;
  className?: string | undefined;
}

const widths = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-2xl', xl: 'max-w-4xl' };

/**
 * Accessible modal: focus moves in on open, Escape and scrim click close,
 * focus is restored on close. No library — the app has one modal pattern.
 */
export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  mode = 'dialog',
  width = 'md',
  className,
}: DialogProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    restoreRef.current = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    const first = panel?.querySelector<HTMLElement>(
      'input,select,textarea,button:not([data-close]),[tabindex]:not([tabindex="-1"])',
    );
    (first ?? panel)?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'Tab' && panel) {
        const focusables = Array.from(
          panel.querySelectorAll<HTMLElement>(
            'input,select,textarea,button,a[href],[tabindex]:not([tabindex="-1"])',
          ),
        ).filter((el) => !el.hasAttribute('disabled'));
        if (focusables.length === 0) return;
        const firstEl = focusables[0]!;
        const lastEl = focusables[focusables.length - 1]!;
        if (e.shiftKey && document.activeElement === firstEl) {
          e.preventDefault();
          lastEl.focus();
        } else if (!e.shiftKey && document.activeElement === lastEl) {
          e.preventDefault();
          firstEl.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      restoreRef.current?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className={cn(
        'fixed inset-0 z-50 flex bg-[var(--scrim)]',
        mode === 'sheet' ? 'justify-end' : 'items-center justify-center p-4',
      )}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        tabIndex={-1}
        className={cn(
          'flex max-h-full w-full flex-col bg-surface text-ink shadow-lg2 outline-none',
          mode === 'sheet'
            ? 'h-full max-w-xl border-l border-line2'
            : cn('rounded-2xl border border-line2', widths[width]),
          className,
        )}
      >
        <div className="flex items-start gap-3 border-b border-line2 px-5 py-4">
          <div className="min-w-0 flex-1">
            <h2 className="font-cond text-lg font-extrabold">{title}</h2>
            {description ? <p className="mt-0.5 text-xs text-ink3">{description}</p> : null}
          </div>
          <Button variant="ghost" size="sm" aria-label="Close" data-close onClick={onClose}>
            <X />
          </Button>
        </div>
        <div className="min-h-0 flex-1 overflow-auto px-5 py-4">{children}</div>
        {footer ? (
          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-line2 px-5 py-3">
            {footer}
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}

export function Sheet(props: Omit<DialogProps, 'mode'>) {
  return <Dialog mode="sheet" {...props} />;
}

export interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: ReactNode;
  children?: ReactNode;
  confirmLabel?: string | undefined;
  danger?: boolean | undefined;
  loading?: boolean | undefined;
}

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  children,
  confirmLabel = 'Confirm',
  danger,
  loading,
}: ConfirmDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      width="sm"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant={danger ? 'danger' : 'primary'} onClick={onConfirm} loading={loading}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="text-[13px] text-ink2">{children}</div>
    </Dialog>
  );
}
